/**
 * The renderer's entire state layer: platform access, settings, theme and the
 * note being edited. Both builds (Electron renderer and web) mount this with a
 * different `Platform` implementation and nothing below it branches again.
 *
 * It is split into five nested providers rather than one. Each provider renders
 * its `children` prop untouched, so a settings change re-renders only the
 * settings consumers — the editor subtree keeps its element identity and
 * CodeMirror is never torn down and rebuilt. Sync status is the newest reason
 * that matters: it changes on its own schedule, and the editor must not re-render
 * because a push landed.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react'
import { byRecency, makeNote } from '@shared/notes'
import {
  DEFAULT_SETTINGS,
  type DeepPartial,
  type Note,
  type NoteId,
  type Platform,
  type Settings,
} from '@shared/types'
import {
  createSyncEngine,
  type AdoptOutcome,
  type PullOutcome,
  type Resolution,
  type ResolutionOutcome,
  type ResolutionTarget,
  type SyncStatus,
} from '@/sync/engine'
import { scratchpadEndpoint } from '@/sync/endpoint'
import { connectLive } from '@/sync/live'
import { guardPull, type PullTarget } from '@/sync/pull-guard'
import {
  syncConfigured,
  syncSettingsKey,
  syncTriggersArmed,
  type SettledSyncSettings,
} from '@/sync/settle'

/** Time the editor stays quiet before a write is issued. */
const SAVE_DEBOUNCE_MS = 400
/** How long "saved" lingers before the indicator goes back to nothing. */
const SAVED_LINGER_MS = 1500
/** Gap between retries after a write failed. Longer than the debounce: a full
 *  disk or a revoked permission does not clear in 400 ms. */
const SAVE_RETRY_MS = 2000
/** Gap between attempts to open the Scratchpad after storage refused to be read.
 *  Same reasoning, and the same order of magnitude. */
const OPEN_RETRY_MS = 2000
/** How long the sync settings must stop changing before sync acts on them. */
const SYNC_SETTINGS_SETTLE_MS = 1000
/**
 * How often an otherwise idle device asks its Jot Server for changes.
 *
 * A minute, which is the shortest interval that is still free: every pass costs one
 * `GET` of the whole Scratchpad, and the panel-open trigger is what actually makes
 * another device's edit appear before the user reads it — a tray app is hidden
 * almost all the time, so the interval is the case where it is *not* hidden and the
 * user is watching. Shorter would buy latency nobody is waiting on; much longer
 * would let two visible panels drift.
 */
const SYNC_POLL_MS = 60_000

export type SaveState = 'idle' | 'saving' | 'saved' | 'error'

/**
 * Jot holds exactly one scratchpad. There is no switcher, no list and no "new
 * note": the panel always opens the same text.
 *
 * The storage layer underneath is still record-based — `NotesStore` lists,
 * creates and tombstones individual notes — so a history could be layered back
 * on without changing how anything is persisted. This type is the narrow view
 * the UI actually needs.
 */
export interface NotesState {
  note: Note | null
  body: string
  saveState: SaveState
  loading: boolean
  /**
   * Storage could not be read, so no Scratchpad is open: what the user types is
   * held in memory, nothing has been written, and the read is being retried.
   *
   * Reported rather than hidden, because it is the one state where the editor
   * accepts text that is not on its way to disk. It is also why there is no `note`
   * to show a timestamp for — a placeholder claiming "updated just now" over a body
   * that is merely out of reach is the most misleading caption available (issue #13).
   */
  unreadable: boolean
  /**
   * Which Note the panel is on, read out of the very ref keystrokes are addressed to.
   *
   * A getter rather than `note.id`, because those were two answers to one question and
   * every disagreement between them has been a defect. `note` is set from a read-back
   * *after* an `await`, so a write that fails leaves it naming the Note the panel has
   * already moved off; the ref moves synchronously and before the write, and it is what
   * `setBody` queues at and `flush` writes to. Anything that needs the *identity* has to
   * ask this one — the sync engine deciding which Note to read, compare, push and record
   * is the whole of that, and it hands the same id to the guard as `comparedId`, which
   * then compares it against this ref again (`@/sync/pull-guard`). Answering that from
   * `note` is how a pull whose write failed left the engine naming the old id while the
   * guard named the new one, so every later pass was refused for the life of the process.
   *
   * What is left of `note` is a projection: the Note storage last handed back, for
   * showing a timestamp and for telling React that something moved. It may lag, and
   * `flush` reconciles it against this ref as writes land; it is never asked who the
   * Scratchpad is.
   *
   * Never null while a Scratchpad is open, and never an id the panel could not commit
   * at: the ref moves in one place and a move that could not be proven is undone
   * (`ScratchpadMove`). That is what lets the engine decide a whole pass from this
   * answer, and what makes this answer disagreeing with Sync State's `scratchpadId`
   * mean one specific thing — a move committed here and not yet recorded there.
   */
  openScratchpadId(): NoteId | null
  setBody(next: string): void
  /**
   * Take on the Scratchpad id Pairing adopted (ADR-0001) and put this session's
   * text there, reporting the Note that resulted. See the implementation: what
   * makes this safe is that the decision and the move happen in one synchronous
   * step, and that the write goes through the ordinary save path.
   */
  adoptScratchpad(id: NoteId): Promise<AdoptOutcome>
  /**
   * Put a Jot Server's body at the Scratchpad, if what the editor holds says that is
   * safe — the guard, which only the panel can evaluate because only the panel knows
   * the editor's live text (`@/sync/pull-guard`).
   *
   * On the interface for the same reason `adoptScratchpad` is: it is the sync layer's
   * one way to reach the editor, and having it here rather than hidden in a ref
   * shared with `SyncProvider` is what keeps that coupling visible.
   */
  applyPulledBody(target: PullTarget): Promise<PullOutcome>
  /**
   * Write the body a Resolution kept, without consulting `guardPull`.
   *
   * On the interface beside `applyPulledBody` and never folded into it: the two are
   * the same write with opposite authorisation, and keeping them apart is what makes
   * the unguarded one impossible to reach by accident (`ResolutionTarget`).
   */
  applyResolvedBody(target: ResolutionTarget): Promise<ResolutionOutcome>
}

interface SettingsState {
  settings: Settings
  update(patch: DeepPartial<Settings>): Promise<void>
}

const PlatformContext = createContext<Platform | null>(null)
const SettingsContext = createContext<SettingsState | null>(null)
const ThemeContext = createContext<'light' | 'dark'>('light')
const NotesContext = createContext<NotesState | null>(null)
/**
 * The status the chrome reports, and the one action the user can take about it.
 *
 * One context and not two, because a Resolution's whole effect is a new status: the
 * pane reads the Conflict out of the status it is rendering and hands the choice
 * back to the same provider, which is the only thing holding the engine.
 */
interface SyncState {
  status: SyncStatus
  /**
   * Keep one body and discard the other (ADR-0002). Resolves to the status the pass
   * left behind, so a caller can tell a Conflict that cleared from one that came
   * straight back because the server moved again.
   */
  resolve(choice: Resolution): Promise<SyncStatus>
}

const SyncContext = createContext<SyncState>({
  status: { kind: 'off' },
  // No provider means no engine, so there is nothing to resolve and nothing to
  // report: the default status says sync is off, and this agrees with it.
  resolve: async () => ({ kind: 'off' }),
})

export function AppProvider({
  platform,
  children,
}: {
  platform: Platform
  children: ReactNode
}): ReactNode {
  return (
    <PlatformContext.Provider value={platform}>
      <SettingsProvider>
        <ThemeProvider>
          <NotesProvider>
            <SyncProvider>{children}</SyncProvider>
          </NotesProvider>
        </ThemeProvider>
      </SettingsProvider>
    </PlatformContext.Provider>
  )
}

export function usePlatform(): Platform {
  const platform = useContext(PlatformContext)
  if (!platform) throw new Error('usePlatform must be used inside <AppProvider>')
  return platform
}

export function useSettings(): SettingsState {
  const settings = useContext(SettingsContext)
  if (!settings) throw new Error('useSettings must be used inside <AppProvider>')
  return settings
}

export function useNotes(): NotesState {
  const notes = useContext(NotesContext)
  if (!notes) throw new Error('useNotes must be used inside <AppProvider>')
  return notes
}

export function useResolvedTheme(): 'light' | 'dark' {
  return useContext(ThemeContext)
}

/** What the last sync attempt did, for the one row of chrome that reports it. */
export function useSync(): SyncStatus {
  return useContext(SyncContext).status
}

/** The Resolution half of the same context, for the pane that offers the choice. */
export function useResolveConflict(): (choice: Resolution) => Promise<SyncStatus> {
  return useContext(SyncContext).resolve
}

/* ------------------------------------------------------------------ settings */

/** Shallow spread would drop the other `sync` keys, hence the explicit branch. */
function mergeSettings(base: Settings, patch: DeepPartial<Settings>): Settings {
  return { ...base, ...patch, sync: { ...base.sync, ...patch.sync } }
}

function SettingsProvider({ children }: { children: ReactNode }): ReactNode {
  const platform = usePlatform()
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS)

  useEffect(() => {
    let alive = true
    void platform.settings.get().then((loaded) => {
      if (alive) setSettings(loaded)
    })
    // The tray menu and a second window can both write settings; the broadcast
    // is the only way this renderer hears about it.
    const unsubscribe = platform.onSettingsChanged(setSettings)
    return () => {
      alive = false
      unsubscribe()
    }
  }, [platform])

  const update = useCallback(
    async (patch: DeepPartial<Settings>) => {
      // Applied optimistically so switches and selects move under the pointer
      // instead of waiting on a round trip to the main process.
      setSettings((current) => mergeSettings(current, patch))
      const persisted = await platform.settings.set(patch)
      setSettings(persisted)
    },
    [platform],
  )

  const value = useMemo<SettingsState>(() => ({ settings, update }), [settings, update])
  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>
}

/* --------------------------------------------------------------------- theme */

function subscribeToSystemTheme(onChange: () => void): () => void {
  const query = window.matchMedia('(prefers-color-scheme: dark)')
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}

function readSystemTheme(): 'light' | 'dark' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function ThemeProvider({ children }: { children: ReactNode }): ReactNode {
  const { settings } = useSettings()
  // The OS preference is an external store in the literal sense: it lives
  // outside React and pushes changes. useSyncExternalStore is exactly its shape.
  const systemTheme = useSyncExternalStore(subscribeToSystemTheme, readSystemTheme)
  const resolved = settings.theme === 'system' ? systemTheme : settings.theme

  // The one and only place the theme is applied to the document.
  useLayoutEffect(() => {
    document.documentElement.classList.toggle('dark', resolved === 'dark')
  }, [resolved])

  return <ThemeContext.Provider value={resolved}>{children}</ThemeContext.Provider>
}

/* --------------------------------------------------------------------- notes */

/**
 * Unsaved bodies, keyed by note id. Still a map rather than one slot even
 * though only one note is ever open: a write that failed stays queued for the
 * next attempt, and the key is what proves a retry lands on the note the text
 * was actually typed into.
 */
type PendingWrites = Map<NoteId, string>

/**
 * What became of a move of `activeIdRef` — and there are only two things it can be.
 *
 * `activeIdRef` is the id a keystroke lands at; Sync State's `scratchpadId` is the id
 * this device has agreed with a Jot Server about. A move left in place without a Note
 * at it puts those two on different Notes, and this defect has now been fixed four
 * times by fixing whichever mover was found to have forgotten the undo — the move in
 * `commitScratchpad`, then `adoptScratchpad`, then `applyPulledBody`, then `pair`'s
 * exits. The undo is therefore not a rule a mover follows. There is one mover, and
 * this is the only pair of answers it can give: the ref is at `note.id` and storage
 * has been read holding that Note, or the ref is back where it was and nothing moved
 * at all. "Moved, and nothing at it" is not a value that exists.
 */
type ScratchpadMove =
  /**
   * The ref is at `note.id`, and this is the Note storage was read holding there —
   * holding a body this panel accounts for, which is the whole of the claim
   * (`commitScratchpad`). Bytes any other writer left at that id are not this outcome.
   */
  | { kind: 'committed'; note: Note }
  /**
   * No Note this panel can account for was read back at the id, so the move has been
   * undone: the ref names the Note it named before, the key the move queued its body
   * under names it too, and the panel is where the sync engine last saw it.
   *
   * Both of those, because a move is both of them. `activeIdRef` is where the *next*
   * keystroke goes and the key in `pendingRef` is where already-typed text goes, and an
   * undo that gave back only the first left a keystroke that arrived inside the move's
   * window queued under the abandoned key — the newest text written to a Note nothing
   * ever opens while the Scratchpad kept a stale body. See `commitScratchpad`, which
   * enumerates the window instant by instant.
   *
   * What is *not* withdrawn is the move's own body when nothing was typed: the save path
   * owns it, requeues it and retries it on its own timer (`flush`), and that retry is the
   * recovery every later pass leans on — it is what puts this session's text at the id,
   * so the next move there can be proven. The write lands *over* whatever is there:
   * nothing, or a Note this device abandoned at that id before — never the Scratchpad,
   * which is the id the panel is back on, and never a Note anything opens, since Jot has
   * no list, no switcher and no search (ADR-0001). What lands is the panel's own text, so
   * no Scratchpad text is at risk. This is also the write that stocks that id for the
   * next move to it, which is the whole reason `commitScratchpad` compares bodies instead
   * of counting Notes.
   *
   * "One duplicate Note and no text" was the whole of what this used to claim, and it is
   * true of exactly that case. When something *was* typed inside the window it cost the
   * text that was typed, which is why the two halves are stated apart now: the editor
   * having moved is what tells them apart, and it is the one fact a landed keystroke
   * cannot hide.
   */
  | { kind: 'reverted' }

/**
 * What Sync State says about the Scratchpad, for a device that has paired.
 *
 * Three fields and not just the id, because across a launch this record and note
 * storage are the *only* surviving evidence about which Note the last session was
 * typing into — `activeIdRef` is gone with the process. See `openedOverRecorded`,
 * which is the one place the other two are read.
 */
interface RecordedScratchpad {
  id: NoteId
  /** The Last-Agreed Body, or null when there is no agreement on record. */
  agreedBody: string | null
  /** Whether a Pending Conflict is recorded, which changes what the id means. */
  conflicted: boolean
}

/**
 * The Scratchpad Sync State records, or null when this device is unpaired.
 *
 * Every failure collapses to null — no record, an unreadable file, a store that
 * rejects outright — because a device that cannot read its Sync State is
 * indistinguishable from one that never had any, and ADR-0001 keeps recency as
 * the bootstrap path for exactly that case. This is also why the call is wrapped
 * here rather than left to the bootstrap's own `catch`: that one deliberately
 * leaves `loading` true forever, which is right for unreachable note storage and
 * would be catastrophic for a corrupt sync file.
 */
async function recordedScratchpad(platform: Platform): Promise<RecordedScratchpad | null> {
  try {
    const state = await platform.syncState.get()
    if (state.scratchpadId === null) return null
    return {
      id: state.scratchpadId,
      agreedBody: state.lastAgreed?.body ?? null,
      conflicted: state.pendingConflict !== null,
    }
  } catch (error) {
    console.error('jot: could not read the sync state', error)
    return null
  }
}

/**
 * The Note to open when the recorded id names one and another Note is newer.
 *
 * `activeIdRef` does not survive a launch, so a move the panel committed and this
 * device never recorded — the Sync State write failed, or the process died between
 * the two (`runOnce`) — leaves nothing that says so directly. The recorded id and
 * storage are all there is, and between them they say one thing: **every save goes to
 * `activeIdRef`** (`flush`), so a panel that stayed on the recorded id would have left
 * it the newest Note in storage. It is not the newest, so either the panel was writing
 * somewhere else, or something outside Jot wrote a Note — ADR-0004 makes these plain
 * files and `IPC.openNotesFolder` invites exactly that.
 *
 * The Last-Agreed Body is what tells those two apart as far as local evidence can: the
 * recorded Note still holds it, so nothing has been typed there since the two sides
 * agreed, while a newer Note holds something else. A device that had merely been typing
 * at the recorded id fails that on its first keystroke, so recency is not being widened
 * — it is being consulted for one state that has no other witness, which is what
 * ADR-0001 leaves room for. Everything else opens the recorded id, unconditionally.
 *
 * **A Pending Conflict stops this outright.** `recordConflict` writes the panel's own id
 * with the Conflict, and `runOnce` then returns at the top until a Resolution (#7) — so
 * the recorded id is already the right one *and* nothing will ever come along to correct
 * it. It is also a state this rule would otherwise fire in constantly: the Conflict the
 * pull guard raises is recorded over storage holding the body the two agreed on, which
 * is this rule's own precondition, and any duplicate an earlier reverted move abandoned
 * is newer. Overriding it would undo the fix that made a Conflict keep the text
 * reachable at all.
 *
 * **The cost, stated because it cannot be discriminated.** A *reverted* re-keying pull
 * leaves disk byte-identical to a committed one: the queued pulled body lands at the
 * abandoned id on the save path's retry, so that id is newest and holds a Jot Server's
 * body, while the recorded id still holds the Last-Agreed Body. This rule fires there
 * too and opens a Note the panel had given back. What it costs is one push of bytes this
 * device only received: the next pass finds a local body differing from the agreement
 * and pushes it at the revision it agreed at, which the server has passed, so the `409`
 * carries the record whose body is those very bytes and `recordPush` records the
 * agreement rather than a Conflict. Only a server that moved *again* in between turns
 * that into a Conflict over a body the user never touched — the same window
 * `unprovenPull` describes and deliberately does not persist across a launch. Left to
 * the recorded id instead, that sub-case re-pulls cleanly; there is no local fact that
 * separates it from the committed move, and losing a whole session's text is the worse
 * of the two.
 *
 * `lastAgreed === null` with a recorded id is deliberately *not* admitted. It is the
 * shape `asLastAgreed` leaves behind, and with no agreement there is nothing that could
 * say the recorded Note has been left alone — so the rule would fire on any stray newer
 * Note, which is the divergence ADR-0001 exists to prevent. The move it misses there
 * needs a dropped agreement *and* a failed Sync State write, and the next pass re-pairs.
 *
 * The ranking is forgeable, which is the other half of the same cost. `parseNoteFile`
 * reads `updatedAt` out of the file's header rather than its mtime, so a Note dropped
 * into the folder carrying a header timestamp of its own can be "newer" without having
 * been written later — the weakness ADR-0001's consequences described as no longer able
 * to swap the user's text, which this makes able to again in the one state above. What
 * keeps it narrow is the rest of the rule: the recorded Note must still hold the
 * Last-Agreed Body, the newer one must hold something else, and no Pending Conflict may
 * be on record. Fixing that field is still the real repair and still a separate one.
 *
 * Every read here is wrapped, because none of it may make a readable Scratchpad
 * unreadable: a rejection would reach `run`'s catch, which parks the user's text in
 * memory and retries for as long as the failure lasts. A `list` that cannot be read is
 * simply no evidence of a newer Note.
 */
async function openedOverRecorded(
  platform: Platform,
  recorded: RecordedScratchpad,
  atRecordedId: Note,
): Promise<Note> {
  if (recorded.conflicted) return atRecordedId
  if (recorded.agreedBody === null || atRecordedId.body !== recorded.agreedBody) {
    return atRecordedId
  }

  try {
    const [mostRecent] = (await platform.notes.list()).sort(byRecency)
    // No other Note, the recorded one *is* the newest, or a tie — a tie is not evidence,
    // and the recorded id is what the tie-break has to fall back to.
    if (!mostRecent || mostRecent.id === recorded.id) return atRecordedId
    if (mostRecent.updatedAt <= atRecordedId.updatedAt) return atRecordedId

    const newer = await platform.notes.get(mostRecent.id)
    // Equal bodies are nothing to choose between, so the recorded id keeps it: a copy of
    // the Scratchpad dropped into the folder is the ordinary way to reach that.
    if (newer === null || newer.body === atRecordedId.body) return atRecordedId
    return newer
  } catch (error) {
    console.error('jot: could not look for a newer scratchpad', error)
    return atRecordedId
  }
}

/**
 * One body from two pieces of the user's own text: what was typed before the
 * store answered, then a blank line so the two do not run into one paragraph,
 * then what the note already held. Nothing is dropped and nothing is rewritten.
 */
function leadWith(preBoot: string, trailing: string): string {
  if (preBoot === '') return trailing
  if (trailing === '') return preBoot
  return `${preBoot}\n\n${trailing}`
}

function NotesProvider({ children }: { children: ReactNode }): ReactNode {
  const platform = usePlatform()

  const [note, setNote] = useState<Note | null>(null)
  const [body, setBodyState] = useState('')
  const [saveState, setSaveState] = useState<SaveState>('idle')
  const [loading, setLoading] = useState(true)
  const [unreadable, setUnreadable] = useState(false)

  // Everything the save path needs lives in refs: a debounced timer and a
  // `beforeunload` handler must see the latest text without being re-bound on
  // every keystroke.
  const activeIdRef = useRef<NoteId | null>(null)
  const bodyRef = useRef('')
  const pendingRef = useRef<PendingWrites>(new Map())
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lingerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Tail of the write chain. Awaiting it means "all queued writes landed". */
  const writesRef = useRef<Promise<void>>(Promise.resolve())
  const bootstrappedRef = useRef(false)
  /** Armed only while storage could not be read; see the bootstrap effect. */
  const openRetryRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /**
   * Text typed before the store had a note to put it in. The editor is live
   * from the first paint — that is the whole point of the app — so the
   * keystrokes that beat the bootstrap have to be parked somewhere.
   */
  const preBootRef = useRef<string | null>(null)
  /** Lets the failure retry re-enter `flush` without depending on itself. */
  const flushRef = useRef<() => Promise<void>>(() => Promise.resolve())
  /**
   * The body a pull last put in the editor, or null once the user has typed.
   *
   * It is the guard's answer to one ordering and nothing else — a pull whose write
   * did not land leaves the editor ahead of storage with bytes that came from the Jot
   * Server, and the next pass must not read that as the user having typed. See
   * `PanelText.appliedPull`, which is where the whole argument lives.
   */
  const pulledRef = useRef<string | null>(null)

  /**
   * The panel arriving at its Scratchpad, which is the only other thing that assigns
   * `activeIdRef` — and the one that cannot leave it disagreeing with Sync State.
   *
   * Nothing is written here, so there is nothing that could fail to land and nothing to
   * undo: this is the bootstrap deciding which Note the panel *is* on, from the recorded
   * id when there is one and from recency when there is not (ADR-0001). Every move after
   * it goes through `commitScratchpad`.
   */
  const openNote = useCallback((opened: Note) => {
    activeIdRef.current = opened.id
    bodyRef.current = opened.body
    setNote(opened)
    setBodyState(opened.body)
    setSaveState('idle')
  }, [])

  // Stable for ever, which is what lets the engine hold it across a run: it reads the
  // ref at the moment it is called rather than closing over a value.
  const openScratchpadId = useCallback(() => activeIdRef.current, [])

  const markSaved = useCallback(() => {
    setSaveState('saved')
    if (lingerRef.current !== null) clearTimeout(lingerRef.current)
    lingerRef.current = setTimeout(() => {
      lingerRef.current = null
      setSaveState('idle')
    }, SAVED_LINGER_MS)
  }, [])

  const cancelDebounce = useCallback(() => {
    if (debounceRef.current !== null) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
  }, [])

  /**
   * Persist whatever is queued and resolve once every write issued so far has
   * landed. Every operation that changes which note is open awaits this first —
   * dropping a keystroke on a switch is the one bug that would sink the app.
   */
  const flush = useCallback((): Promise<void> => {
    cancelDebounce()

    const pending = [...pendingRef.current]
    if (pending.length === 0) return writesRef.current

    pendingRef.current = new Map()
    setSaveState('saving')

    // Writes are chained, never parallel: two overlapping updates to the same
    // note can otherwise land out of order and resurrect older text.
    const chained = writesRef.current
      .catch(() => undefined)
      .then(async () => {
        let failed = false

        for (const [id, body] of pending) {
          try {
            const saved = await platform.notes.update(id, body)
            // Reconciled against `activeIdRef`, not against whatever the projection
            // happened to hold: the ref is the id this write was addressed to, so a Note
            // that landed at it *is* the Scratchpad's current record. Asking whether the
            // projection already named that id is what let the two drift apart for good —
            // a pull or a Pairing move whose first write failed leaves `note` on the old
            // id, and this predicate, which is the only thing that ever repaired it, could
            // no longer match.
            if (id === activeIdRef.current) setNote(saved)
          } catch (error) {
            console.error('jot: could not save a note', error)
            failed = true
            // Requeue, unless a newer keystroke already replaced this body.
            if (!pendingRef.current.has(id)) pendingRef.current.set(id, body)
          }
        }

        if (failed) {
          // Nothing else retries on its own, and an unsaved note that reports
          // itself as idle is indistinguishable from a saved one.
          setSaveState('error')
          if (debounceRef.current === null) {
            debounceRef.current = setTimeout(() => {
              debounceRef.current = null
              void flushRef.current()
            }, SAVE_RETRY_MS)
          }
          return
        }

        // Another keystroke may have queued a newer body while this was in
        // flight; claiming "saved" then would be a lie.
        if (pendingRef.current.size === 0) markSaved()
      })

    writesRef.current = chained
    return chained
  }, [cancelDebounce, markSaved, platform])

  useEffect(() => {
    flushRef.current = flush
  }, [flush])

  /**
   * Point the panel at `id`, put `body` there, and report what became of the move —
   * the one move both things the sync layer asks of the panel are made of, and the
   * only thing in Jot that moves `activeIdRef` once a Scratchpad is open.
   *
   * Everything before the first `await` is the safety argument, and what matters
   * about it is exactly that: nothing is awaited between the caller reading
   * `activeIdRef`/`bodyRef` and this moving them. An `async` function body runs
   * synchronously up to its first `await`, so caller and helper are one step and no
   * keystroke fits between the decision and the commit. That is what Pairing's
   * previous shape lacked, and why it needed a refusal that arrived too late to undo
   * a Note the engine had already written. It is also the whole of what makes the pull
   * guard a compare-and-swap rather than a check, so the two callers share it: stated
   * once, it cannot drift into being true of only one of them.
   *
   * Queueing rather than writing directly is deliberate. The save path is where
   * writes are chained so they cannot land out of order, requeued and retried when
   * they fail, and drained synchronously on `beforeunload`. None of that is
   * re-implemented here and none of it is perturbed: this queues a body and flushes,
   * which is what a settled keystroke does.
   *
   * `body` is null for the caller that only needs the panel to follow an id it is
   * already on — Pairing re-pairing after losing its Sync State, where no Note moves
   * and none is written.
   *
   * Anything already queued at the old id still writes there, under its own key.
   * That is not a loss: `setBody` queues the *whole* body, so the newest text is
   * what goes to the id being taken up, and the old Note keeps a copy of what it had.
   *
   * What the read-back has to be evidence *of* is this panel's write, and a Note being
   * present at the id is not that. `flush` reports a failed write by requeueing it rather
   * than by throwing, so what storage answers is all there is to go on — and an id that
   * already has a Note at it is the ordinary case rather than the exotic one, because this
   * design plants those Notes: a move that is undone deliberately leaves its queued body
   * to land at the id it abandoned (`ScratchpadMove`), so every reverted move stocks the
   * id that the next move to it would read as its own. Taken as proof, that Note is
   * `committed`, the ref stays moved, and `pair` pushes `adopted.note.body` — text no
   * device holds — to every paired device.
   *
   * So the bodies are compared, as the Conflict predicate and `unprovenPull` compare
   * bodies: the read-back is proof when it holds bytes this panel put at that id, which is
   * to say when storage is indistinguishable from what a landed write would have left.
   * Two readings qualify, and they are the two writes that can own the id:
   *
   * - the body queued here — the write landed;
   * - the body the editor holds *now*, read after the awaits rather than captured before
   *   them: a keystroke arriving during the flush replaces the queued body under the same
   *   key and its write can be the one that lands (`setBody`), which is the ordering
   *   `unprovenPull.queuedOver` models on the engine's side.
   *
   * Anything else is somebody else's Note, and the move is **undone**: the ref names the
   * Note it named before, which is the recovery `main` used to get by accident — the next
   * pass reads the id the panel is still on, finds the body it has always found there, and
   * makes the same move again. Being strict costs nothing, and a coincidence is not a
   * mistake: bytes equal to the panel's are the bytes a landed write would have left,
   * whoever wrote them.
   *
   * With no body to queue there is no write that could have landed, so the editor's own
   * text is the only reading left. That caller is the panel following an id it is already
   * on, where the undo is a no-op and being refused costs one pass.
   *
   * Reporting the write's outcome instead of reading storage back was the alternative, and
   * it is worse three ways: `flush` is the ordinary save path, so a per-id outcome would
   * have to be threaded through the chain every keystroke goes down; both callers need the
   * `Note` regardless, so a read happens either way; and a write status cannot tell "mine
   * failed" from "a keystroke replaced my queued body and won", which is the second
   * reading above and is proof rather than a failure.
   *
   * `previous` is a `NoteId` and not `NoteId | null` on purpose. Both callers refuse
   * before this when the panel has nothing open, so an undo can never leave the panel
   * with no Scratchpad — and the type is what keeps that true of a fifth caller.
   *
   * The move is **two** state changes and the undo gives back both. `activeIdRef` is
   * where the next keystroke goes; the key in `pendingRef` is where already-typed text
   * goes. Both are set here in one synchronous step and both are unset in one, so there
   * is no instant at which the panel is typing at one Note and queueing at another.
   * Managing only the first is what this got wrong for six rounds: every keystroke
   * arriving inside the window was queued under the key the move was about to abandon.
   *
   * Text typed at each instant of the window, and where it ends up:
   *
   * - **Before the move.** Queued at `previous` by `setBody`, and drained by the `flush`
   *   below into a write at `previous`. Untouched by any of this.
   * - **Between the move and the drain.** No such instant: the two assignments and
   *   `flush`'s own `pendingRef.current = new Map()` all run before the first `await`,
   *   which is the same fact that makes the pull guard a compare-and-swap.
   * - **While the writes are in flight, and the move commits.** The keystroke is queued
   *   at `id`, which is the Scratchpad from here on. Nothing to move.
   * - **While the writes are in flight, and the move reverts.** The keystroke is queued
   *   at `id` and `id` is being abandoned, so the entry is re-keyed to `previous` — the
   *   id it would have been queued at had the move never happened.
   * - **While the writes are in flight, and the keystroke's own 400 ms debounce fires
   *   before the read-back.** Its write lands at `id`, so `pendingRef` holds nothing to
   *   re-key. That is why the rule below reads the *editor* rather than the map: the
   *   editor is the one place a landed keystroke still is. (A landed write is normally
   *   proof and commits the move; this instant is reachable when `notes.get` then throws.)
   * - **After the read-back.** No such instant either — the predicate and the undo are
   *   synchronous, so the last keystroke the window can hold is one during the `get`.
   * - **After the undo.** The ref is back, so `setBody` queues at `previous`. Ordinary.
   *
   * `bodyRef.current !== body` is the discriminator, and it is exact rather than a
   * heuristic: both callers pass the body the editor is holding at the instant they call
   * — `adoptScratchpad` passes `bodyRef.current` itself, `applyPulledBody` assigns it
   * first — so the two are equal on entry and can only differ because `setBody` ran.
   *
   * A write already queued at `previous` is replaced rather than merged. It can only be
   * one the `flush` above failed to land and requeued, so it is older than the editor's
   * text, and `setBody` queues the *whole* body: the newest one supersedes it, and one
   * entry per id is what keeps the write chain from issuing two writes that could land in
   * either order.
   */
  const commitScratchpad = useCallback(
    async (previous: NoteId, id: NoteId, body: string | null): Promise<ScratchpadMove> => {
      activeIdRef.current = id
      if (body !== null) pendingRef.current.set(id, body)

      await flushRef.current()

      let stored: Note | null
      try {
        stored = await platform.notes.get(id)
      } catch (error) {
        console.error('jot: could not read the scratchpad back after writing it', error)
        stored = null
      }

      if (stored !== null && (stored.body === body || stored.body === bodyRef.current)) {
        setNote(stored)
        return { kind: 'committed', note: stored }
      }

      activeIdRef.current = previous

      if (previous !== id && bodyRef.current !== body) {
        // Typed inside the window, so this is the text the Scratchpad has to end up
        // holding — and `previous` is the Scratchpad again.
        //
        // The move's own body is not here to be re-keyed alongside it, and cannot be:
        // reaching this branch means `setBody` ran past its early return, and that call
        // wrote the typed text at `activeIdRef` — which was `id`. So the entry this drops
        // *is* the keystroke's, `body` was overwritten under the same key before it could
        // be written, and the abandoned id keeps whatever storage already held. The
        // stocking that the other branch leaves behind (`ScratchpadMove`) is exactly the
        // case where nothing was typed and `body` is therefore still queued at `id`.
        pendingRef.current.delete(id)
        pendingRef.current.set(previous, bodyRef.current)

        // Nothing else would issue this one. A revert normally follows a write that
        // failed, and `flush` arms its own retry for that — but the instant above where
        // the keystroke's write landed and `notes.get` then threw leaves no timer at all,
        // and a queued body no timer is coming for is text held only in memory.
        if (debounceRef.current === null) void flushRef.current()
      }

      return { kind: 'reverted' }
    },
    [platform],
  )

  /**
   * Re-point the editor at the Scratchpad id Pairing adopted, and put this
   * session's text there.
   *
   * `bodyRef.current`, not a body handed in: text typed while Pairing was deciding
   * is exactly the text at risk, and it is the editor that has it.
   *
   * The editor's text is deliberately not replaced — it is the source of what was
   * just written, and pushing a value in would move the caret for no reason. That is
   * the one line where this and the pull differ, and it is the difference between the
   * two: adopting moves the user's own bytes to a new id, while pulling replaces them
   * with somebody else's.
   */
  const adoptScratchpad = useCallback(
    async (id: NoteId): Promise<AdoptOutcome> => {
      const open = activeIdRef.current
      if (open === null) return { kind: 'unopened' }
      const move = await commitScratchpad(open, id, open === id ? null : bodyRef.current)
      return move.kind === 'committed'
        ? { kind: 'adopted', note: move.note }
        : { kind: 'unwritten' }
    },
    [commitScratchpad],
  )

  /**
   * Write a Jot Server's body over the Scratchpad, if the guard says that is safe.
   *
   * The guard is asked here and nowhere else, because here is the only place the
   * facts it needs exist: the editor's live text, and whether the save path has
   * anything queued. Asked *and answered* in the same synchronous step as the write
   * it authorises — the verdict, the editor replacement and the queue all happen
   * before the first `await` — which is what makes it a compare-and-swap. A version
   * that asked the panel "may I?" and then wrote would leave a gap exactly the size
   * of the keystroke it exists to protect.
   *
   * The editor *is* replaced here, unlike in `adoptScratchpad`, and the caret moving
   * is unavoidable: the text under it has been replaced by a different body, which is
   * what a pull is. The acceptance criterion about the caret is about the other case —
   * a pull arriving while the user types touches nothing at all, because the guard
   * refuses and this returns before any state moves.
   *
   * `bodyRef` and `pulledRef` are set together and before the write, not after it: the
   * editor has to show what was pulled whether or not the write lands, and `pulledRef`
   * is what tells the next pass that the difference between the two is not something
   * the user typed.
   *
   * The editor is what is *not* undone when the move is (`ScratchpadMove`), and the two
   * are separable for that reason: the pulled bytes came from a Jot Server, so an editor
   * holding them is not a local change and the next pass recognises it — where an
   * `activeIdRef` left at an id storage holds nothing at is a fact about identity that
   * nothing else can read back.
   */
  const applyPulledBody = useCallback(
    async (target: PullTarget): Promise<PullOutcome> => {
      // Ahead of the guard, and with the guard's own answer to it: a null `openId` is
      // `unopened` there too, and asking first is what leaves an id to commit at
      // afterwards. The rule still lives in one place — this is the panel declining to
      // ask a question it has no Scratchpad for, not a second copy of the decision.
      const open = activeIdRef.current
      if (open === null) return { kind: 'refused', because: 'unopened' }

      const verdict = guardPull(
        {
          openId: open,
          liveBody: bodyRef.current,
          queuedWrites: pendingRef.current.size > 0,
          appliedPull: pulledRef.current,
        },
        target,
      )
      if (verdict !== 'apply') return { kind: 'refused', because: verdict }

      bodyRef.current = target.body
      pulledRef.current = target.body
      setBodyState(target.body)

      const move = await commitScratchpad(open, target.id, target.body)
      return move.kind === 'committed'
        ? { kind: 'applied', note: move.note }
        : { kind: 'unwritten' }
    },
    [commitScratchpad],
  )

  /**
   * Write the body the user kept over the Scratchpad. No guard (ADR-0002, #7).
   *
   * The one write in Jot that replaces the editor's text without first proving the
   * editor has not moved — and the reason is in `ResolutionTarget`: the proof
   * `guardPull` asks for is false by construction in a Conflict, so a Resolution run
   * through it is a Resolution that never happens. The authorisation is the click,
   * on a button whose label says which body goes.
   *
   * What is still owed is ordering, which `commitScratchpad` gives: the body goes in
   * through the ordinary save path, so it cannot land under a write already queued.
   * And `pulledRef` is set for the same reason a pull sets it — these bytes came from
   * a Jot Server, so the next pass must not read them as something the user typed.
   *
   * Text typed between the preview and the click is discarded with the rest of the
   * local body, which is what the click asked for; it is not singled out and it is
   * not rescued, because a Resolution that kept some of the discarded side would be
   * the merge ADR-0002 says this project does not do.
   */
  const applyResolvedBody = useCallback(
    async (target: ResolutionTarget): Promise<ResolutionOutcome> => {
      const open = activeIdRef.current
      // Nothing open, so there is no Scratchpad to write at and no move to commit.
      // The engine leaves the Conflict recorded on this answer, which is right: the
      // body the user chose to discard is still the only copy on this device.
      if (open === null) return { kind: 'unwritten' }

      bodyRef.current = target.body
      pulledRef.current = target.body
      setBodyState(target.body)

      const move = await commitScratchpad(open, target.id, target.body)
      return move.kind === 'committed'
        ? { kind: 'applied', note: move.note }
        : { kind: 'unwritten' }
    },
    [commitScratchpad],
  )

  const setBody = useCallback(
    (next: string) => {
      if (next === bodyRef.current) return

      // The user has taken over whatever a pull put in the editor, so it stops being a
      // reason to forgive the editor for being ahead of storage. Cleared after the
      // early return above and not before it: CodeMirror mirrors `body` back through
      // this callback, so a pull's own replacement arrives here as a no-op and must not
      // be counted as a keystroke.
      pulledRef.current = null

      // Local state is authoritative while typing; storage catches up later.
      bodyRef.current = next
      setBodyState(next)

      const id = activeIdRef.current
      if (id === null) {
        // No note yet. Hold the text — dropping it here is how the first thing
        // a user types after a cold start disappears.
        preBootRef.current = next
        return
      }

      pendingRef.current.set(id, next)

      cancelDebounce()
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null
        void flush()
      }, SAVE_DEBOUNCE_MS)
    },
    [cancelDebounce, flush],
  )

  useEffect(() => {
    // StrictMode runs mount effects twice in development. Without this guard a
    // first run would create two empty notes instead of one.
    if (bootstrappedRef.current) return
    bootstrappedRef.current = true

    /**
     * Open the Scratchpad, or throw because storage could not be read.
     *
     * Throwing is the whole shape of the failure handling: nothing below writes
     * anything until it has a Note in hand, so an exception here means the editor is
     * live with no note open, which is a state `setBody` already knows — it parks the
     * text in `preBootRef`, exactly as it does for keystrokes that beat a cold start.
     */
    const attempt = async (): Promise<void> => {
      // Which note the panel opens is an identity, not a ranking (ADR-0001):
      // Sync State names the Scratchpad once this device has paired. It is null
      // on every install that has never synced — the common case, and the reason
      // the recency fallback below must stay.
      const recorded = await recordedScratchpad(platform)
      const scratchpadId = recorded?.id ?? null

      let existing: Note | null = null

      if (recorded === null) {
        // Unpaired: the scratchpad is simply the most recently touched note.
        // Older records may exist — from a previous version, or restored by
        // hand into the notes folder — and they are left alone rather than
        // merged. This path must never be removed: an install that never
        // syncs cannot depend on a sync file to find its own scratchpad.
        const [mostRecent] = (await platform.notes.list()).sort(byRecency)
        existing = mostRecent ? await platform.notes.get(mostRecent.id) : null
      } else {
        // Paired: the recorded id, unless storage carries the one piece of evidence that
        // the last session had moved off it and never got to say so (`openedOverRecorded`).
        const atRecordedId = await platform.notes.get(recorded.id)
        existing =
          atRecordedId === null
            ? null
            : await openedOverRecorded(platform, recorded, atRecordedId)
      }

      // Anything typed while the store was answering is parked in
      // `preBootRef`. Where it goes depends on which note is opening, and the
      // one thing every branch has to guarantee is that neither it nor the
      // opened note's body is dropped.
      const typed = preBootRef.current === '' ? null : preBootRef.current
      let opened: Note | null = null
      /**
       * Body the opened note already held, when the pre-boot text has to go in
       * ahead of it rather than instead of it. Null whenever the text simply
       * *is* the body of the note that opened.
       */
      let trailingBody: string | null = null

      if (existing !== null && typed === null) {
        openNote(existing)
      } else if (existing !== null) {
        // The scratchpad that opened, plus keystrokes that beat the read. Both
        // texts stay, in one body, at that note's own id — because the two
        // alternatives are worse. Putting the keystrokes in a note of their
        // own, which is what this did before, strands a whole session at an id
        // nothing ever opens again: identity bootstrap reopens the recorded
        // note next launch, and Jot has no list, no switcher and no search that
        // could surface the other one. Recording the new id instead would
        // re-identify the scratchpad of an already paired device over a stray
        // keystroke at launch, manufacturing a pairing conflict out of nothing.
        //
        // Nothing is discarded and nothing is rewritten, so this is not a
        // Resolution — that word belongs to the sync flow, where the user picks
        // one body and the other is thrown away.
        //
        // This used to apply only to a *recorded* id, leaving the recency fallback
        // to mint a second Note for the keystrokes — which is the same stranding,
        // and the retry below is a new way to reach it: an unpaired install whose
        // note was briefly unreadable would come back with the text typed during
        // the failure in a fresh Note, and the note it was typed instead of
        // unreachable. Where the id came from makes no difference to the merge, so
        // it no longer decides it.
        openNote(existing)
        opened = existing
        trailingBody = existing.body
      } else if (scratchpadId !== null) {
        // A recorded id with no note at it — genuinely no note, which is now the
        // only way a null gets here: both stores answer null for absent (the web
        // one for a tombstone too) and reject when a read failed, and a rejection
        // is handled by the caller of this function instead (issue #12). The
        // difference is the whole of what makes writing here safe. An empty body
        // written at an id whose file was merely out of reach would rename a fresh
        // note over the real one; written at an id that has nothing, it costs
        // nothing at all.
        //
        // Even so, bootstrap opens the id and writes nothing: the record is local
        // until the user types, and the ordinary save path then upserts it at that
        // same id, so the identity survives a missing local copy without
        // manufacturing an empty body for Pairing to raise a Conflict over.
        //
        // Reaching here at all means something went wrong: Pairing records an id
        // only once a Note has been read back from storage at it (see `pair` in
        // `sync/engine` and `adoptScratchpad` above), so a recorded id normally
        // *does* have a local copy.
        const placeholder: Note = { ...makeNote(), id: scratchpadId }
        openNote(placeholder)
        opened = placeholder
      } else {
        const created = await platform.notes.create(typed ?? undefined)
        openNote(created)
        opened = created
      }

      // `openNote` has just replaced the editor's text, so replay whatever is
      // parked now — later keystrokes included, which is why this reads
      // `preBootRef` again instead of reusing `typed`. The pre-boot text leads
      // `trailingBody`: MarkdownEditor keeps the caret at its offset across a
      // replacement from props, so text at the front leaves the caret inside
      // what the user was typing, and text appended after a long body would
      // both move the caret into somebody else's sentence and scroll the user's
      // own words off screen, which reads as having lost them.
      //
      // `preBootRef` is cleared here and nowhere else: on the retry path below
      // there is no note to replay into, and clearing it there would drop the only
      // copy of what the user has typed.
      const latest = preBootRef.current
      preBootRef.current = null
      if (opened !== null && latest !== null) {
        const next = trailingBody === null ? latest : leadWith(latest, trailingBody)
        if (next !== opened.body) setBody(next)
      }

      setLoading(false)
      setUnreadable(false)
    }

    /**
     * Try, and keep trying while storage refuses to be read.
     *
     * `loading` deliberately stays true throughout: the rest of the app may assume a
     * non-null note the moment loading ends, and it is also the gate `SyncProvider`
     * waits on — so an unreadable Scratchpad cannot be pushed either, before the
     * engine's own read even comes into it.
     *
     * What is held rather than written is the user's text, and holding it is the
     * point. Writing it at the recorded id is what destroyed the real body: the
     * placeholder read as an empty Scratchpad, so typing into it was the expected
     * thing to do, and the first keystroke upserted the id with one character. Both
     * stores now refuse that write, and this refuses to ask for it. The retry is what
     * makes refusing honest — when the read succeeds, the branches above open the real
     * note and lead it with everything typed meanwhile, which is the same rule the
     * pre-boot keystrokes follow.
     *
     * The cost is that text typed while unreadable is only in memory, so a quit loses
     * it. That is the trade being made deliberately: the alternative is writing it
     * over a body that is still there.
     */
    const run = (): void => {
      void attempt().catch((error: unknown) => {
        console.error('jot: could not open the scratchpad', error)
        setUnreadable(true)
        openRetryRef.current = setTimeout(() => {
          openRetryRef.current = null
          run()
        }, OPEN_RETRY_MS)
      })
    }

    run()
    // No cleanup: StrictMode unmounts and remounts, and `bootstrappedRef` means the
    // second mount does not start a second attempt — cancelling on the first
    // teardown would leave the panel with no note at all. The retry timer is cleared
    // with the others when the tree really goes away.
  }, [openNote, platform, setBody])

  // `platform.onNotesChanged` is deliberately not subscribed. With a single
  // scratchpad there is no list to refresh, and the only other thing the event
  // could drive is re-reading the body — which would race a burst of typing and
  // overwrite the user's text with a stale copy. Edits made in another editor
  // are picked up the next time the panel opens.
  //
  // That race is the one a pull would run too, which is why a pull is guarded and this
  // is not merely unsubscribed: `@/sync/pull-guard` is what an event handler here has
  // no way to be, since a file-watcher event carries no body and no evidence about
  // whether the editor is ahead of what it is reporting.

  useEffect(() => {
    // Hiding the panel is the normal way to leave Jot, and it fires
    // visibilitychange rather than beforeunload — both paths must save.
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') void flush()
    }

    const onBeforeUnload = (): void => {
      // Not `flush()`: that resolves the write chain first, so the `update`
      // call would not be issued until a microtask at the earliest, and on
      // quit the renderer is already being torn down. Calling the backend
      // straight from the handler posts the IPC message synchronously, so the
      // main process receives it whether or not this frame survives. Ordering
      // still holds — the main store serialises writes per note.
      cancelDebounce()
      const pending = [...pendingRef.current]
      pendingRef.current = new Map()
      for (const [id, body] of pending) {
        void platform.notes.update(id, body).catch(() => undefined)
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('beforeunload', onBeforeUnload)
    }
  }, [cancelDebounce, flush, platform])

  useEffect(() => {
    return () => {
      if (debounceRef.current !== null) clearTimeout(debounceRef.current)
      if (lingerRef.current !== null) clearTimeout(lingerRef.current)
      if (openRetryRef.current !== null) clearTimeout(openRetryRef.current)
    }
  }, [])

  const value = useMemo<NotesState>(
    () => ({
      note,
      body,
      saveState,
      loading,
      unreadable,
      openScratchpadId,
      setBody,
      adoptScratchpad,
      applyPulledBody,
      applyResolvedBody,
    }),
    [
      note,
      body,
      saveState,
      loading,
      unreadable,
      openScratchpadId,
      setBody,
      adoptScratchpad,
      applyPulledBody,
      applyResolvedBody,
    ],
  )

  return <NotesContext.Provider value={value}>{children}</NotesContext.Provider>
}

/* ---------------------------------------------------------------------- sync */

/**
 * When the sync engine gets to run, and nothing else.
 *
 * Everything the engine decides it decides for itself — it reads Settings, Sync
 * State and the persisted Scratchpad on each pass — so this provider owns exactly
 * two things: the moments worth a look, and the status the StatusBar reports. That is
 * deliberate: the interesting behaviour then lives in a module a plain Node process
 * can drive against a real server, and none of it depends on a React tree being
 * mounted.
 *
 * Pull did not change that division, only the list of moments: a trigger still says
 * "look", never "pull" or "push". Which of the two a pass makes is the engine's answer
 * to what it finds, so no trigger can be wrong about it, and the panel-open and
 * interval triggers below are not a pull schedule — they are the two times nothing
 * else would have woken it.
 *
 * The save path above is untouched. It is load-bearing in ways that are easy to
 * break — a debounce, a chain of writes that must not run in parallel, a requeue
 * on failure, a synchronous `beforeunload` — and sync reads its outcome rather
 * than hooking into it. A pulled body goes *through* it rather than around it, for
 * the same reason (`commitScratchpad`).
 */
function SyncProvider({ children }: { children: ReactNode }): ReactNode {
  const platform = usePlatform()
  const { settings } = useSettings()
  const {
    note,
    loading,
    saveState,
    openScratchpadId,
    adoptScratchpad,
    applyPulledBody,
    applyResolvedBody,
  } = useNotes()
  const [status, setStatus] = useState<SyncStatus>({ kind: 'off' })

  const engine = useMemo(
    () =>
      createSyncEngine({
        settings: platform.settings,
        syncState: platform.syncState,
        // Narrowed to `get` alone by the engine's own dep type. Both writes are the
        // panel's — `adoptScratchpad` and `applyPulledBody` below — so the engine holds
        // no way to persist a body at all, which is how "no body from a Jot Server is
        // written by the sync layer" stays structural rather than a convention now that
        // there is a pull to write one.
        notes: platform.notes,
        info: platform.info,
        // The panel's own answer, out of the ref the editor is addressed through, and not
        // `note?.id` — which is what this used to read. ADR-0001 keeps recency as the
        // bootstrap path, so before Pairing the panel is the only thing that knows which
        // Note is the Scratchpad; the id it decides all of a pass from therefore has to be
        // the one a keystroke would land at, or the guard it hands that id to is answering
        // about a different Note (`NotesState.openScratchpadId`).
        openScratchpadId,
        // All three stable across renders — their only dependency is `commitScratchpad`,
        // whose only dependency is `platform`, which is this dependency list's too — so
        // naming them here does not rebuild the engine and abandon a push in flight.
        // None may ever close over `note` or `body`: they read the editor out of refs
        // precisely so that they can be named here without changing on every keystroke.
        adoptScratchpad,
        applyPulledBody,
        applyResolvedBody,
      }),
    [adoptScratchpad, applyPulledBody, applyResolvedBody, openScratchpadId, platform],
  )

  /**
   * A counter rather than the `saveState` value, because only the rising edge is
   * a reason to sync: `saved` lingers and then clears itself, and syncing again
   * as it clears would cost a settings read, a Sync State read and a file read
   * for an answer that cannot have changed.
   *
   * `saved` is the signal to use because of what `flush` promises by it — it is
   * set only once every queued write has landed *and* nothing new is pending. A
   * push therefore never races the save path for the same body.
   */
  const [savedCount, setSavedCount] = useState(0)
  useEffect(() => {
    if (saveState === 'saved') setSavedCount((count) => count + 1)
  }, [saveState])

  // Which settings a pass is worth reading, and what it takes to arm a trigger, are
  // both stated in `@/sync/settle` rather than here: they decide when this device spends
  // a credential, and the plumbing around them is the part no runner can hold still.
  const settingsKey = syncSettingsKey(settings.sync)
  const configured = syncConfigured(settings.sync)

  /**
   * The settings trigger, once the fields have stopped changing.
   *
   * Every keystroke in the token field is a settings write, and every one of them would
   * otherwise be an authenticated request with a token that is not finished. What that
   * costs, and where a partial one goes, is the whole of `@/sync/settle`.
   *
   * A settled save and a newly opened Scratchpad do not wait: both are already the end
   * of something. The interval and the visibility trigger below do, through this
   * snapshot — `configured` as it stood when the fields went quiet, never read live.
   */
  const [settled, setSettled] = useState<SettledSyncSettings>(() => ({
    key: settingsKey,
    configured,
  }))
  useEffect(() => {
    if (settingsKey === settled.key) return
    const timer = setTimeout(
      () => setSettled({ key: settingsKey, configured }),
      SYNC_SETTINGS_SETTLE_MS,
    )
    return () => clearTimeout(timer)
  }, [settingsKey, settled.key, configured])

  /**
   * The two triggers pull adds, as one counter: the panel becoming visible, and a
   * timer for the rest of the time.
   *
   * Both are gated on sync being configured *and settled* (`syncTriggersArmed`), which
   * is the whole of "sync off costs nothing" plus the half of it these two triggers
   * would otherwise have broken: armed off the live fields they run from the first
   * character of a token, and the engine reads settings fresh on every pass, so a tick
   * spends a real request carrying a partial credential — to whatever host the endpoint
   * field holds just then.
   *
   * A counter rather than calling `engine.sync()` from the handlers, so every trigger
   * goes through the one effect that owns `live` and `setStatus` — a status from an
   * engine this provider has since replaced describes a device configuration that no
   * longer exists.
   *
   * `visibilitychange` and not a desktop-only bridge event: the panel being shown from
   * the tray is a visibility change, hiding it is already the save path's signal
   * (`NotesProvider`), and the web build gets the same behaviour without faking an
   * event it never fires.
   */
  const armed = syncTriggersArmed(settingsKey, settled)
  const [pullCount, setPullCount] = useState(0)
  useEffect(() => {
    if (!armed) return

    const look = (): void => setPullCount((count) => count + 1)
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') look()
    }

    const timer = setInterval(look, SYNC_POLL_MS)
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [armed])

  /**
   * The push channel (ADR-0005), additive to the poll and visibility triggers above:
   * it feeds the same `pullCount`, never `engine.sync()` directly, for the reason
   * given where `pullCount` is declared — only the effect below may own `live` and
   * `setStatus`. A device the channel never reaches (a proxy that blocks streaming,
   * a token being retyped) still has the poll, which is why this has no status of
   * its own to surface.
   *
   * Depends on `[armed, settled.key]` rather than on `settings.sync.endpoint`/`.token`
   * directly: `armed` being true means the live settings already equal `settled`
   * (`syncTriggersArmed`), so reading them inside the effect is reading the same
   * values `settled.key` names, without making every keystroke recreate the
   * connection before it has even settled.
   */
  useEffect(() => {
    if (!armed) return

    const resolved = scratchpadEndpoint(settings.sync.endpoint)
    if (!resolved.ok) return

    const connection = connectLive(
      { url: `${resolved.url}/events`, token: settings.sync.token },
      () => setPullCount((count) => count + 1),
    )
    return () => connection.close()
  }, [armed, settled.key])

  // The id, not the Note: the record is replaced on every landed write, and
  // depending on it would run this effect twice for one save.
  //
  // The projection here, deliberately, and it is the one use of it that survives being a
  // projection: a trigger is a *reason to look*, so one that arrives late costs a moment
  // and one that never arrives costs nothing the poll below does not already cover. Which
  // Note the pass then decides everything about is `openScratchpadId`'s answer, read when
  // the engine runs rather than when React last rendered.
  const scratchpadId = note?.id ?? null

  useEffect(() => {
    // Nothing to push before the bootstrap has settled on a Note, and asking the
    // engine then would only find the recency fallback half-done.
    if (loading || scratchpadId === null) return

    let live = true
    void engine.sync().then((next) => {
      // A status from an engine this effect no longer owns describes a device
      // configuration that has since changed.
      if (live) setStatus(next)
    })

    return () => {
      live = false
    }
    // A failed push has no retry timer of its own and still does not need one: the
    // interval above is what pull required anyway, and a pass is a pass — whichever
    // trigger woke it, the engine decides again from scratch whether there is anything
    // to send.
  }, [engine, loading, scratchpadId, savedCount, settled.key, pullCount])

  /**
   * The status is set from the pass's own answer rather than left to a trigger, which
   * is what lets the pane close the moment a Resolution lands instead of on the next
   * interval. The answer is returned as well, so the caller knows whether the Conflict
   * cleared or a newer one replaced it.
   */
  const resolve = useCallback(
    async (choice: Resolution): Promise<SyncStatus> => {
      const next = await engine.resolve(choice)
      setStatus(next)
      return next
    },
    [engine],
  )

  const value = useMemo<SyncState>(() => ({ status, resolve }), [status, resolve])

  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>
}
